import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { Helmet as MetaTags } from 'react-helmet';
import Catalog from '~/Catalog';
import EmailNotificationsBlock, {
  EmailNotificationsFormData,
} from '~/components/organism/EmailNotificationsBlock';
import ContentContainerDefault from '~/components/molecule/ContentContainer';
import withErrorBoundary from '~/hocs/withErrorBoundary';
import useCurrentUser from '~/hooks/useCurrentUser';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useUsers from '~/hooks/useUsers';
import SynchronisedMailboxesBlock from './components/SynchronisedMailboxesBlock';
import { emailNotificationsFormDataToMutation } from '~/components/organism/EmailNotificationsBlock/util';
import {
  useGetUserQuery,
  UserRole,
  useUpdateMyDetailsMutation,
  useUpdateOfficeRelationMutation,
} from '~/graphql/types';
import AppErrorScreen from '~/components/template/AppErrorScreen';
import Loading from '~/components/atom/Loading';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import ProfileHeader from './components/ProfileHeader';
import FormContainer from './components/FormContainer';
import SignatureContainer from '~/components/organism/SignatureContainer';
import { asUserDetailsFormData } from './utils/asUserDetailsFormData';
import { convertOfficesList } from './utils/convertOfficesList';
import useIsMounted from '~/hooks/useIsMounted';
import { SectionContainer } from './Profile.style';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import useSessionHydration from '~/hooks/useSessionHydration';
import usePermissions from '~/hooks/usePermissions';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import createPageTitle from '~/util/createPageTitle';
import useTrackEvent from '~/hooks/useTrackEvent';

export type UserDetailsFormData = {
  name: string;
  email: string;
  phone: string;
  selectedMainOfficeId: string;
};

type Props = {
  hasError?: boolean;
} & RouteComponentProps;

const text = {
  pageTitle: 'Profiel',
  imageUploadError:
    'Er is iets mis gegaan bij het uploaden van het bestand. Probeer het nog een keer.',
  phoneValidation: Catalog.invalidPhone,
  userUpdateError: Catalog.genericUnknownErrorMessage,
  title: 'Profiel',
  submitBtn: 'Wijzig wachtwoord',
  deleteLabel: 'Verwijder profiel',
  signature: 'Handtekening gebruiker',
  notificationSuccessMessage: 'E-mail notificaties succesvol geüpdate',
};

const Profile: React.FCC<Props> = ({ hasError }) => {
  const [, refetchSessionHydration] = useSessionHydration();
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
  const [cachedOfficeId, setCachedOfficeId] = useState<string | null>(null);
  const [cachedPhone, setCachedPhone] = useState<string | null>(null);
  const addToast = useAddToast();
  const hasEmailPermission = usePermissions(['root.email.user']);
  const trackEvent = useTrackEvent();

  const [updateMyDetailsMutation, { error: detailsMutationError }] =
    useUpdateMyDetailsMutation();
  const [updateOfficeRelationMutation, { error: officeMutationError }] =
    useUpdateOfficeRelationMutation();

  const me = useCurrentUser();

  const { id: accountId } = useCurrentAccount();

  const { data, networkStatus, refetch, startPolling, stopPolling } =
    useGetUserQuery({
      variables: {
        userId: me.id,
        accountId,
      },
    });

  const accountAdmins = useUsers().filter(
    user => user.role === UserRole.Admin && user.__typename !== 'DeletedUser',
  );

  const isLastAccountAdmin =
    accountAdmins.length === 1 && accountAdmins[0].id === me.id;

  const isComponentMounted = useIsMounted();

  const onSetInitialDataLoaded = () => {
    setInitialDataLoaded(true);
  };

  const onShowErrorMessage = (msg: string) =>
    addToast([formatToastMessage(msg, 'danger')]);

  const onSetCachedData = (data: {
    cachedPhone?: string | null;
    cachedOfficeId?: string | null;
  }) => {
    setCachedOfficeId(
      data.cachedOfficeId === undefined ? cachedOfficeId : data.cachedOfficeId,
    );
    setCachedPhone(
      data.cachedPhone === undefined ? cachedPhone : data.cachedPhone,
    );
  };

  const onUserUpdateError = () => {
    onShowErrorMessage(text.userUpdateError);
    onSetCachedData({
      cachedPhone: null,
    });
  };

  if (hasError) return <AppErrorScreen />;
  if (isInitialLoadStatus(networkStatus)) return <Loading />;
  if (!data || !data.getUser) return <AppErrorScreen />;

  const user = data.getUser;

  const mailboxes = user.Mailboxes;
  const signature = user.Signature ?? null;

  const refetchUser = () => processRefetch();

  const onRelationMutationError = () => {
    void processRefetch();
    onSetCachedData({ cachedOfficeId: null });
    onShowErrorMessage(text.userUpdateError);
  };

  const processRefetch = async () => {
    if (!initialDataLoaded) onSetInitialDataLoaded();

    const { data } = await refetch();
    const { getUser } = data;
    if (getUser === null) {
      return;
    }

    if (cachedPhone && cachedPhone === getUser?.phone) {
      onSetCachedData({
        cachedPhone: null,
      });
    }

    const updatedCachedOffice = getUser?.Offices.find(
      item => item.id === cachedOfficeId,
    );

    if (
      updatedCachedOffice != null &&
      updatedCachedOffice.relation != null &&
      updatedCachedOffice.relation.mainOffice === true
    ) {
      onSetCachedData({
        cachedOfficeId: null,
      });
    }

    if (!isComponentMounted) return;

    await refetchSessionHydration();
  };

  if (detailsMutationError) onUserUpdateError();

  const updateMyName = (name: string) =>
    updateMyDetailsMutation({
      variables: {
        accountId,
        update: {
          name,
        },
      },
    });

  const updateUserDetails = (newFormData: UserDetailsFormData) => {
    const { phone, name } = newFormData;

    onSetCachedData({
      cachedPhone: phone,
    });

    return updateMyDetailsMutation({
      variables: {
        accountId: accountId,
        update: {
          name,
          phone,
        },
      },
    }).then(result => {
      if (result.data && !result.errors) {
        /** ----- TRACKING: profile_updated ----- */
        trackEvent({
          eventName: 'profile_updated',
        });
      }
      return result;
    });
  };

  const updateEmailNotifications = (
    newFormData: EmailNotificationsFormData,
  ) => {
    const vars = emailNotificationsFormDataToMutation(newFormData);

    return updateMyDetailsMutation({
      variables: {
        accountId,
        update: vars,
      },
    }).then(data => {
      // We only check for success here, because failure is checked within `detailsMutationError`
      if (data) {
        addToast([
          formatToastMessage(text.notificationSuccessMessage, 'success'),
        ]);
        /** ----- TRACKING: profile_updated ----- */
        trackEvent({
          eventName: 'profile_updated',
        });
      }
    });
  };

  if (officeMutationError) onRelationMutationError();

  const updateMainOffice = (officeId: string): Promise<any> => {
    const update = { mainOffice: true };

    onSetCachedData({
      cachedOfficeId: officeId,
    });

    return updateOfficeRelationMutation({
      variables: {
        accountId,
        officeId,
        userId: me.id,
        update,
      },
    });
  };

  const userFormData = {
    email: user ? user.email.toLowerCase() : '',
    phone: cachedPhone || user ? user.phone : '',
    cachedOfficeId,
  };

  const initialUserDetailValues = asUserDetailsFormData(user);

  const onUserDetailsFormSubmit = (formNewData: UserDetailsFormData) => {
    void updateUserDetails(formNewData);

    // check is office role realy changed and call mutation if true
    const selectedOfficeId = formNewData.selectedMainOfficeId;
    const officeInitial = user.Offices.find(
      item => item.id === selectedOfficeId,
    );

    if (officeInitial) {
      if (
        officeInitial.relation &&
        officeInitial.relation.mainOffice !== true
      ) {
        return updateMainOffice(selectedOfficeId); // id of changed office
      }
    }
    return;
  };

  const onEmailNotificationsFormSubmit = (
    formNewData: EmailNotificationsFormData,
  ) => updateEmailNotifications(formNewData);

  return (
    <ContentContainerDefault>
      <MetaTags>
        <title>{createPageTitle(text.pageTitle)}</title>
      </MetaTags>
      <section data-testid="user-profile-page">
        <ProfileHeader
          user={user}
          me={me}
          refetchUser={refetchUser}
          isLastAccountAdmin={isLastAccountAdmin}
          updateMyName={updateMyName}
        />
        <SectionContainer>
          <LeftContainer>
            <FormContainer
              userFormData={userFormData}
              officesList={convertOfficesList(user)}
              onUserDetailsFormSubmit={onUserDetailsFormSubmit}
              initialUserDetailsValues={initialUserDetailValues}
              refetch={refetch}
              signature={signature}
              userId={me.id}
            />

            {hasEmailPermission.allowed && (
              <SignatureContainer
                title={text.signature}
                signature={signature}
                refetch={refetch}
                entityTypeForMutation={{ userId: me.id }}
              />
            )}
          </LeftContainer>

          <div>
            {hasEmailPermission.allowed && (
              <SynchronisedMailboxesBlock
                mailboxes={mailboxes}
                startPolling={startPolling}
                stopPolling={stopPolling}
                refetch={() => refetch().then(() => refetchSessionHydration())}
              />
            )}

            <EmailNotificationsBlock
              onFormSubmit={onEmailNotificationsFormSubmit}
              notificationSettings={user.NotificationSettings}
            />
          </div>
        </SectionContainer>
      </section>
    </ContentContainerDefault>
  );
};

const LeftContainer = styled.div<{}>`
  ${({ theme }) => css`
    width: 100%;

    ${theme.mq.greaterThan('desktop')`
      width: 50%;
    `}
  `}
`;

export default withErrorBoundary(Profile);
